import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData, createHowToStructuredData } from '../../utils/structuredData';

export default function AppGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sho-navi.com';

  // SEO設定
  usePageSEO({
    title: 'DMM FXアプリ使い方完全ガイド｜13種類のテクニカル指標搭載',
    description: 'DMM FXスマホアプリの使い方を徹底解説。13種類のテクニカル指標、4画面分割チャート、スピード注文など高機能を搭載。初心者でも使いやすいインターフェースで、外出先でも本格的なFX取引が可能です。',
    keywords: 'DMM FX, アプリ, スマホ, 使い方, テクニカル指標, チャート分析, モバイル取引',
    ogImage: 'https://public.readdy.ai/ai/img_res/895bc251944154a8f68ef2975f15a381.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXアプリ使い方完全ガイド',
        description: 'DMM FXスマホアプリの機能と使い方を詳しく解説。13種類のテクニカル指標、4画面チャート、注文方法など、初心者でも分かりやすく説明します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20app%20guide&width=1200&height=630&seq=app-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: `${siteUrl}/app-guide`
      }),
      // アプリの使い方HowTo構造化データ
      createHowToStructuredData({
        name: 'DMM FXアプリの使い方',
        description: 'DMM FXアプリをダウンロードして取引を始めるまでの手順を4ステップで解説します。',
        totalTime: 'PT10M',
        steps: [
          {
            name: 'アプリをダウンロード',
            text: 'App StoreまたはGoogle Playで「DMM FX」を検索してダウンロード。無料でインストールできます。iOS 12.0以降、Android 6.0以降に対応しています。',
            image: 'https://readdy.ai/api/search-image?query=smartphone%20app%20store%20download%20screen&width=800&height=600&seq=app-howto-step1&orientation=landscape'
          },
          {
            name: 'ログイン・生体認証設定',
            text: '口座開設済みの方はログインIDとパスワードでログイン。指紋認証・顎認証を設定すれば、次回からスムーズにログインできます。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20app%20login%20screen%20with%20biometric%20authentication&width=800&height=600&seq=app-howto-step2&orientation=landscape'
          },
          {
            name: 'チャートを確認・分析',
            text: 'レート一覧から気になる通貨ペアをタップしてチャートを表示。テクニカル指標を追加して相場を分析しましょう。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20app%20chart%20analysis%20screen%20with%20technical%20indicators&width=800&height=600&seq=app-howto-step3&orientation=landscape'
          },
          {
            name: '注文・決済',
            text: 'スピード注文でワンタップ発注、または注文画面から詳細な注文設定が可能。ポジション画面で損益を確認し、決済しましょう。',
            image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20app%20order%20screen%20with%20speed%20trading&width=800&height=600&seq=app-howto-step4&orientation=landscape'
          }
        ]
      }),
      // FAQ構造化データ
      createFAQStructuredData([
        {
          question: 'DMM FXアプリはどのOSに対応していますか?',
          answer: 'DMM FXアプリはiOS 12.0以降とAndroid 6.0以降に対応しています。App StoreまたはGoogle Playから無料でダウンロードできます。'
        },
        {
          question: 'アプリでもPC版と同じ機能が使えますか?',
          answer: 'はい、DMM FXアプリは13種類のテクニカル指標、4画面分割チャート、各種注文方法など、PC版と同等の機能を搭載しています。外出先でも本格的な取引が可能です。'
        },
        {
          question: 'アプリで入出金はできますか?',
          answer: 'はい、アプリからクイック入金と出金申請が可能です。約340の金融機関に対応しており、クイック入金なら手数料無料で即時反映されます。'
        },
        {
          question: 'アプリの使い方が分からない場合はどうすればいいですか?',
          answer: 'DMM FXは24時間サポート体制を整えています。電話、メール、LINEで問い合わせが可能です。アプリ内にもヘルプ機能があり、操作方法を確認できます。'
        },
        {
          question: 'デモアプリで練習できますか?',
          answer: 'はい、「DMM FX バーチャル」という別アプリで、仮想資金500万円を使ってリスクゼロで練習できます。本番と同じ操作感で取引を体験できます。'
        }
      ], `${siteUrl}/app-guide`),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'アプリガイド', url: '/app-guide' }
      ], siteUrl)
    ]
  });

  const [activeTab, setActiveTab] = useState<'trend' | 'oscillator'>('trend');

  const smartphoneBenefits = [
    {
      icon: 'ri-time-line',
      title: '24時間いつでも取引',
      description: '通勤中、休憩時間、寝る前など、スキマ時間を活用して取引できます。PCを開く必要がありません。'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'どこでも取引可能',
      description: '自宅、カフェ、外出先など場所を選ばず取引。急な相場変動にもすぐに対応できます。'
    },
    {
      icon: 'ri-notification-3-line',
      title: 'リアルタイム通知',
      description: '経済指標発表やレート到達をプッシュ通知でお知らせ。チャンスを逃しません。'
    },
    {
      icon: 'ri-speed-line',
      title: 'ワンタップで即発注',
      description: 'スピード注文機能で素早く発注。急な相場変動時も瞬時に対応できます。'
    },
    {
      icon: 'ri-shield-check-line',
      title: '生体認証で安全',
      description: '指紋・顔認証でセキュアにログイン。パスワード入力の手間なく安全に取引開始。'
    },
    {
      icon: 'ri-wallet-3-line',
      title: '入出金もアプリで完結',
      description: 'クイック入金・出金申請もスマホで完結。約340の金融機関に対応しています。'
    }
  ];

  const trendIndicators = [
    { name: '移動平均線', description: '相場のトレンド方向を把握' },
    { name: '指数平滑移動平均線', description: '直近の価格を重視した移動平均' },
    { name: '一目均衡表', description: '日本発の総合的なテクニカル指標' },
    { name: 'ボリンジャーバンド', description: '価格の変動幅を視覚化' },
    { name: 'パラボリック', description: 'トレンド転換点を示唆' },
    { name: 'スパンモデル', description: '一目均衡表を発展させた指標' }
  ];

  const oscillatorIndicators = [
    { name: 'MACD', description: '移動平均の収束・拡散を分析' },
    { name: 'RSI', description: '買われすぎ・売られすぎを判断' },
    { name: 'DMI/ADX', description: 'トレンドの強さを測定' },
    { name: 'ストキャスティクス', description: '相場の過熱感を分析' },
    { name: 'RCI', description: '順位相関による売買判断' },
    { name: 'ROC', description: '価格変化率を測定' },
    { name: '移動平均乖離率', description: '移動平均からの乖離を分析' }
  ];

  const orderTypes = [
    { name: '成行注文', description: '現在のレートで即座に発注', icon: 'ri-flashlight-line' },
    { name: '指値注文', description: '指定レートで自動発注', icon: 'ri-price-tag-3-line' },
    { name: '逆指値注文', description: '損切り・利確を自動化', icon: 'ri-arrow-left-right-line' },
    { name: 'IFD注文', description: '新規と決済を同時に設定', icon: 'ri-links-line' },
    { name: 'OCO注文', description: '2つの注文を同時に発注', icon: 'ri-git-branch-line' },
    { name: 'IFO注文', description: 'IFDとOCOを組み合わせ', icon: 'ri-organization-chart' }
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'DMM FX取引ガイド', url: '/fx-trade' },
            { name: 'アプリガイド', url: '/app-guide' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20professional%20forex%20trading%20app%20interface%20with%20candlestick%20charts%20and%20financial%20data%20on%20screen%2C%20clean%20minimalist%20background%20with%20soft%20teal%20and%20white%20gradient%2C%20professional%20business%20atmosphere%2C%20high%20quality%20product%20photography%20style&width=1920&height=800&seq=app-hero-1&orientation=landscape"
            alt="DMM FXアプリ"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 rounded-full px-4 py-2 mb-6">
              <i className="ri-smartphone-line text-teal-400"></i>
              <span className="text-teal-300 text-sm font-medium">スマホだけで本格FX取引</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              DMM FXアプリ<br />使い方完全ガイド
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              PCがなくても大丈夫。DMM FXアプリなら、スマホひとつで口座開設から取引、入出金まですべて完結。<strong className="text-red-400 font-bold text-xl">13種類のテクニカル指標</strong>と<strong className="text-red-400 font-bold text-xl">4画面チャート</strong>で本格的な分析も可能です。
            </p>
            
            <CTAButton />
            
            <div className="flex items-center gap-6 mt-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-apple-fill text-lg"></i>
                <span>iOS対応</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-android-fill text-lg"></i>
                <span>Android対応</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-lg"></i>
                <span>最短即日取引</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smartphone Benefits */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">BENEFITS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              スマホだけで取引する<span className="text-teal-600">6つのメリット</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              DMM FXアプリは、PCと同等の機能をスマホで実現。いつでもどこでも本格的なFX取引ができます。
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {smartphoneBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-5">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{benefit.title}</span>
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots - Trading Screen */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">TRADING SCREEN</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              直感的な<span className="text-emerald-600">取引画面</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              シンプルで見やすいデザイン。初心者でも迷わず操作できる直感的なインターフェースです。
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border-l-4 border-teal-500">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-dashboard-line text-teal-600"></i>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-base">レート一覧画面</span>
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    全通貨ペアのリアルタイムレートを一覧表示。スプレッドや前日比も一目で確認できます。お気に入り登録で素早くアクセス。
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border-l-4 border-emerald-500">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-flashlight-line text-emerald-600"></i>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-base">スピード注文</span>
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    チャートを見ながらワンタップで発注。ドテン注文やFIFO注文（古い建玉から順に自動決済）にも対応しています。
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border-l-4 border-teal-500">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-teal-600"></i>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-base">ポジション管理</span>
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    保有ポジションの損益をリアルタイムで確認。一括決済や個別決済もスムーズに行えます。
                  </p>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-gray-900 rounded-3xl p-3 shadow-2xl">
                  <div className="rounded-2xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20forex%20trading%20mobile%20app%20interface%20showing%20currency%20pair%20list%20with%20real-time%20rates%20USD%20JPY%20EUR%20GBP%2C%20clean%20modern%20UI%20design%20with%20green%20and%20red%20price%20indicators%2C%20dark%20theme%20trading%20screen%2C%20high%20quality%20app%20screenshot%20mockup&width=600&height=900&seq=app-trading-1&orientation=portrait"
                      alt="DMM FX取引画面"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chart Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">CHART ANALYSIS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              高機能<span className="text-teal-600">チャート分析</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              <span className="text-red-600 font-bold text-xl">13種類のテクニカル指標</span>と<span className="text-red-600 font-bold text-xl">最大4画面分割表示</span>。PCに匹敵する本格的なチャート分析がスマホで可能です。
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-gray-900 rounded-3xl p-3 shadow-2xl">
                  <div className="rounded-2xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20forex%20trading%20chart%20interface%20on%20mobile%20app%20showing%20candlestick%20chart%20with%20technical%20indicators%20moving%20average%20bollinger%20bands%20MACD%2C%20dark%20theme%20with%20green%20and%20red%20candles%2C%20clean%20modern%20trading%20UI%20design&width=600&height=900&seq=app-chart-1&orientation=portrait"
                      alt="DMM FXチャート画面"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">チャート機能の特徴</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-layout-grid-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      <span className="text-red-600 font-bold text-lg">4画面分割表示</span>
                    </h4>
                    <p className="text-sm text-gray-600">複数の通貨ペアや時間足を同時に監視。効率的な分析が可能です。</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      <span className="text-red-600 font-bold text-lg">11種類の時間足</span>
                    </h4>
                    <p className="text-sm text-gray-600">1分足から月足まで、様々な時間軸でチャートを分析できます。</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-edit-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">描画ツール</h4>
                    <p className="text-sm text-gray-600">トレンドラインやフィボナッチ・リトレースメントなどの描画機能を搭載。</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-md">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-cursor-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">チャート注文</h4>
                    <p className="text-sm text-gray-600">チャート画面から直接注文可能。分析しながらすぐに発注できます。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 利用者の声へのリンク */}
          <div className="bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>

          {/* Technical Indicators */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              搭載テクニカル指標<span className="text-red-600 font-bold text-2xl">（全13種類）</span>
            </h3>
            
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('trend')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'trend' 
                      ? 'bg-teal-600 text-white shadow-md' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="text-red-600 font-bold text-base">トレンド系（6種類）</span>
                </button>
                <button
                  onClick={() => setActiveTab('oscillator')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'oscillator' 
                      ? 'bg-emerald-600 text-white shadow-md' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="text-red-600 font-bold text-base">オシレーター系（7種類）</span>
                </button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'trend' ? trendIndicators : oscillatorIndicators).map((indicator, index) => (
                <div key={index} className={`rounded-xl p-4 border-2 ${
                  activeTab === 'trend' 
                    ? 'bg-teal-50 border-teal-100' 
                    : 'bg-emerald-50 border-emerald-100'
                }`}>
                  <h4 className="font-bold text-gray-900 mb-1">{indicator.name}</h4>
                  <p className="text-xs text-gray-600">{indicator.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Order Types */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">ORDER TYPES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              多彩な<span className="text-emerald-600">注文方法</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              初心者向けのシンプルな注文から、上級者向けの複合注文まで。あらゆる取引スタイルに対応します。
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orderTypes.map((order, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${order.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{order.name}</span>
                </h3>
                <p className="text-sm text-gray-600">{order.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4-Screen Chart */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-teal-500/20 text-teal-400 text-sm font-semibold px-4 py-1 rounded-full mb-4">MULTI CHART</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              4画面チャートで<span className="text-teal-400">同時監視</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              複数の通貨ペアや時間足を同時に表示。効率的な相場分析で取引チャンスを逃しません。
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-500/30 to-emerald-500/30 rounded-3xl blur-3xl"></div>
            <div className="relative bg-gray-800 rounded-2xl p-4 shadow-2xl">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-900 rounded-xl overflow-hidden aspect-video">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Forex%20candlestick%20chart%20USD%20JPY%20with%20moving%20average%20lines%20on%20dark%20background%2C%20professional%20trading%20interface%2C%20green%20and%20red%20candles%2C%20clean%20minimal%20design&width=400&height=300&seq=chart-1&orientation=landscape"
                    alt="USD/JPYチャート"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-900 rounded-xl overflow-hidden aspect-video">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Forex%20candlestick%20chart%20EUR%20USD%20with%20bollinger%20bands%20indicator%20on%20dark%20background%2C%20professional%20trading%20interface%2C%20green%20and%20red%20candles%2C%20clean%20minimal%20design&width=400&height=300&seq=chart-2&orientation=landscape"
                    alt="EUR/USDチャート"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-900 rounded-xl overflow-hidden aspect-video">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Forex%20candlestick%20chart%20GBP%20JPY%20with%20RSI%20indicator%20below%20on%20dark%20background%2C%20professional%20trading%20interface%2C%20green%20and%20red%20candles%2C%20clean%20minimal%20design&width=400&height=300&seq=chart-3&orientation=landscape"
                    alt="GBP/JPYチャート"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-900 rounded-xl overflow-hidden aspect-video">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Forex%20candlestick%20chart%20AUD%20USD%20with%20MACD%20indicator%20below%20on%20dark%20background%2C%20professional%20trading%20interface%2C%20green%20and%20red%20candles%2C%20clean%20minimal%20design&width=400&height=300&seq=chart-4&orientation=landscape"
                    alt="AUD/USDチャート"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-center gap-8 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="w-3 h-3 bg-teal-500 rounded-full"></span>
                  <span>USD/JPY</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                  <span>EUR/USD</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="w-3 h-3 bg-cyan-500 rounded-full"></span>
                  <span>GBP/JPY</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                  <span>AUD/USD</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Use */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">HOW TO USE</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              アプリの<span className="text-teal-600">使い方</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              ダウンロードから取引開始まで、4つのステップで簡単に始められます。
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-teal-500 to-emerald-600"></div>
              <div className="flex items-start gap-6 pl-4">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">アプリをダウンロード</h3>
                  <p className="text-gray-600 mb-4">
                    App StoreまたはGoogle Playで「DMM FX」を検索してダウンロード。無料でインストールできます。
                  </p>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                      <i className="ri-apple-fill text-xl text-gray-700"></i>
                      <span className="text-sm text-gray-700">iOS 12.0以降</span>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                      <i className="ri-android-fill text-xl text-gray-700"></i>
                      <span className="text-sm text-gray-700">Android 6.0以降</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-teal-500 to-emerald-600"></div>
              <div className="flex items-start gap-6 pl-4">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">ログイン・生体認証設定</h3>
                  <p className="text-gray-600">
                    口座開設済みの方はログインIDとパスワードでログイン。指紋認証・顎認証を設定すれば、次回からスムーズにログインできます。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-teal-500 to-emerald-600"></div>
              <div className="flex items-start gap-6 pl-4">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">チャートを確認・分析</h3>
                  <p className="text-gray-600">
                    レート一覧から気になる通貨ペアをタップしてチャートを表示。テクニカル指標を追加して相場を分析しましょう。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-teal-500 to-emerald-600"></div>
              <div className="flex items-start gap-6 pl-4">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">注文・決済</h3>
                  <p className="text-gray-600">
                    スピード注文でワンタップ発注、または注文画面から詳細な注文設定が可能。ポジション画面で損益を確認し、決済しましょう。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo App */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-teal-50 via-emerald-50 to-teal-50 rounded-2xl p-8 md:p-12 border border-teal-100">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <i className="ri-gamepad-line text-4xl text-white"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">まずはデモ取引で練習</h3>
                <p className="text-gray-600 mb-4">
                  「DMM FX バーチャル」アプリなら、本番と同じ操作感をリスクゼロで体験できます。初心者の方はまずデモ取引で練習してから本番に移行するのがおすすめです。
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <span className="inline-flex items-center gap-1 bg-white rounded-full px-4 py-2 text-sm text-gray-700 shadow-sm">
                    <i className="ri-check-line text-teal-600"></i>
                    仮想資金500万円
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white rounded-full px-4 py-2 text-sm text-gray-700 shadow-sm">
                    <i className="ri-check-line text-teal-600"></i>
                    本番と同じ操作感
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white rounded-full px-4 py-2 text-sm text-gray-700 shadow-sm">
                    <i className="ri-check-line text-teal-600"></i>
                    リスクゼロ
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">24時間サポート対応</h2>
            <p className="text-gray-600">操作に迷ったときも安心。LINEでの問い合わせにも対応しています。</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">電話サポート</h3>
              <p className="text-sm text-gray-600">24時間対応</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">メールサポート</h3>
              <p className="text-sm text-gray-600">24時間受付</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-fill text-2xl text-green-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">LINEサポート</h3>
              <p className="text-sm text-gray-600">気軽に問い合わせ</p>
            </div>
          </div>
        </div>
      </section>

      {/* Message Section */}
      <section className="py-16 bg-gradient-to-r from-teal-50 via-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative">
              <div className="flex items-center justify-center gap-2 mb-6">
                <i className="ri-smartphone-line text-3xl text-teal-600"></i>
                <span className="text-lg font-bold text-teal-600">スマホで始めるFX</span>
              </div>
              
              <p className="text-gray-700 text-center leading-relaxed mb-6">
                DMM FXアプリは、<strong className="text-gray-900">スマホひとつで取引のすべてが完結</strong>する高機能アプリです。<br />
                PCを持っていなくても、通勤中や休憩時間など<strong className="text-gray-900">スキマ時間を活用</strong>して本格的なFX取引ができます。<br />
                まずは公式サイトでアプリの詳細を確認してみてください。
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <i className="ri-check-double-line text-teal-600"></i>
                  口座開設無料
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-check-double-line text-teal-600"></i>
                  最短即日取引開始
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-check-double-line text-teal-600"></i>
                  デモ取引で練習可能
                </span>
              </div>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mt-8 bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            アプリについてさらに詳しく知りたい方へ
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/trading-hours" 
              className="group bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border-2 border-teal-100 hover:border-teal-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    取引時間を確認
                  </h3>
                  <p className="text-sm text-gray-600">
                    アプリでいつでも取引できる時間帯を詳しく解説
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/spread" 
              className="group bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-money-dollar-circle-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    スプレッドは本当に狭い？
                  </h3>
                  <p className="text-sm text-gray-600">
                    アプリでの取引コスト、スプレッドについて詳しく解説
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/beginner-guide" 
              className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-user-star-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                    初心者でも大丈夫？
                  </h3>
                  <p className="text-sm text-gray-600">
                    アプリを使った初心者向けの取引ガイド
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiIjZmZmIiIgZmlsbC1vcGFjaXR5PSIwLjA1Ij48cGF0aCBkPSJNMzYgMzR2Mi1IMjR2LTJoMTJ6TTM2IDI0djJIMjR2LTJoMTJ6Ii8+PC9nPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            スマホで本格FX取引を<br />今すぐ始めよう
          </h2>
          
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            DMM FXアプリなら、いつでもどこでも本格的なFX取引が可能。<br />
            今なら最大<span className="text-amber-300 font-bold">50万円</span>キャッシュバックキャンペーン実施中！
          </p>
          
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
            <i className="ri-gift-line"></i>
            <span>👉 今だけ最大50万円キャッシュバック</span>
          </div>
          <div className="block">
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
            >
              👉 今だけ最大50万円キャッシュバック<br />
              無料でDMM FX口座を開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-teal-100">
            <span className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              最短即日で取引開始
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-money-dollar-circle-line"></i>
              口座開設・維持費無料
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-customer-service-2-line"></i>
              24時間サポート
            </span>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
